/**
 * Positivius Job Description Interactive Features
 * Comprehensive implementation of all interactive elements
 * Scroll Progress, Copy, Share, TOC, Read Time, NEW Badges
 */

// Initialize all features when DOM is ready
document.addEventListener('DOMContentLoaded', initPositiviusJobInteractions);
document.addEventListener('turbo:load', initPositiviusJobInteractions);
// Important: sidebar updates via Turbo frames won't trigger turbo:load
document.addEventListener('turbo:frame-load', initPositiviusJobInteractions);
// Turbo caches the DOM on navigation but does NOT preserve event listeners.
// Every widget this pack injects guards against re-creation (share buttons via
// the `data-share-bound` marker, and the copy button / TOC via exists-checks),
// so a restored cached page keeps the stale, listener-less markup and the init
// functions bail — leaving the controls dead. Transient toasts also get frozen
// mid-display because their removal timers don't survive the restore. Reset all
// of that before caching so everything re-creates and re-binds on restore.
document.addEventListener('turbo:before-cache', () => {
  document.querySelectorAll('[data-share-bound]').forEach((button) => {
    delete button.dataset.shareBound;
  });
  const copyBtn = document.getElementById('positivius-copy-btn');
  if (copyBtn) copyBtn.remove();
  const toc = document.querySelector('.positivius-toc');
  if (toc) toc.remove();
  document.querySelectorAll('.positivius-notification').forEach((el) => el.remove());
});

function initPositiviusJobInteractions() {
  // Share buttons appear in two places: the full job-detail page
  // (`/jobs/:slug`) AND the Turbo-frame sidebar (`_job_sidebar_v3`) rendered
  // on the /jobs search page. The sidebar does NOT render
  // `.positivius-job-description`, so binding must happen BEFORE the
  // job-description guard below — otherwise the sidebar's twitter/linkedin/
  // copy buttons never get wired up. `initShareButtons` is idempotent
  // (it dedups via `data-shareBound`) so calling it on every page is safe.
  initShareButtons();

  // Everything below this point targets the legacy job-detail page
  // (`/jobs/:slug` — see app/views/jobs/job_turbo.html.erb). Bail
  // before doing anything on pages that don't render that template.
  //
  // The scroll-progress bar is the one with teeth: `initScrollProgress`
  // appends a `<div class="positivius-scroll-progress">` to `body` and
  // relies on `_job_content.scss` to position it `fixed`. The app-shell
  // layout does NOT load the legacy CSS bundle, so under the new shell
  // the bar falls back to `position: static`, becomes a flex child of
  // the `flex` body, and its inline `width: N%` (from the scroll
  // handler) eats ~700px out of the right column on scroll — visibly
  // shrinking dashboard / /discord / etc. once the user scrolls.
  if (!document.querySelector('.positivius-job-description')) return;

  cleanupJobDescription();
  initScrollProgress();
  initCopyToClipboard();
  initTableOfContents();
  calculateReadTime();
  initNewBadges();
  initScrollAnimations();
}

/**
 * 0. Cleanup Job Description
 * Remove HTML tags showing as text and fix encoding issues
 */
function cleanupJobDescription() {
  const content = document.querySelector('.positivius-job-description, .cleaned-content');
  if (!content) return;
  
  // Find and remove code blocks that contain HTML tags (not actual code)
  const codeBlocks = content.querySelectorAll('code');
  codeBlocks.forEach(code => {
    const text = code.textContent;
    // If it looks like HTML tags, hide it
    if (text.includes('<h2>') || text.includes('<strong>') || text.includes('</')) {
      code.style.display = 'none';
    }
  });
  
  // Fix encoding issues (â€™ should be ')
  const walker = document.createTreeWalker(
    content,
    NodeFilter.SHOW_TEXT,
    null,
    false
  );
  
  const textNodes = [];
  while (walker.nextNode()) {
    textNodes.push(walker.currentNode);
  }
  
  textNodes.forEach(node => {
    if (node.nodeValue) {
      // Fix common encoding issues
      node.nodeValue = node.nodeValue
        .replace(/â€™/g, "'")
        .replace(/â€œ/g, '"')
        .replace(/â€/g, '"')
        .replace(/â€"/g, '—')
        .replace(/â€"/g, '–')
        .replace(/Â /g, ' ')
        .replace(/â€¦/g, '...');
    }
  });
}

/**
 * 1. Scroll Progress Indicator
 * Shows visual progress as user scrolls through job description
 */
function initScrollProgress() {
  const progressBar = document.querySelector('.positivius-scroll-progress');
  if (!progressBar) {
    const bar = document.createElement('div');
    bar.className = 'positivius-scroll-progress';
    document.body.appendChild(bar);
  }
  
  window.addEventListener('scroll', () => {
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight - windowHeight;
    const scrolled = window.scrollY;
    const progress = (scrolled / documentHeight) * 100;
    
    const bar = document.querySelector('.positivius-scroll-progress');
    if (bar) {
      bar.style.width = `${Math.min(progress, 100)}%`;
    }
  }, { passive: true });
}

/**
 * 2. Copy to Clipboard
 * Floating button to copy job description
 */
function initCopyToClipboard() {
  const jobDescription = document.querySelector('.positivius-job-description, .job-description-content, .cleaned-content');
  if (!jobDescription) return;
  
  // Only show on job pages, not on home page
  if (window.location.pathname === '/' || window.location.pathname === '/home') return;
  
  // Check if button already exists
  if (document.getElementById('positivius-copy-btn')) return;
  
  const copyBtn = document.createElement('button');
  copyBtn.id = 'positivius-copy-btn';
  copyBtn.className = 'positivius-copy-btn';
  copyBtn.setAttribute('aria-label', 'Copy job description');
  copyBtn.innerHTML = `
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" stroke-width="2"/>
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" stroke-width="2"/>
    </svg>
  `;
  
  copyBtn.style.cssText = `
    position: fixed;
    bottom: 32px;
    right: 32px;
    width: 56px;
    height: 56px;
    background: linear-gradient(135deg, #B9FF66 0%, #A3E635 100%);
    border: 3px solid #191A23;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: 0 8px 24px rgba(185, 255, 102, 0.4);
    transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    z-index: 1000;
  `;
  
  copyBtn.addEventListener('click', async () => {
    try {
      const text = jobDescription.innerText;
      await navigator.clipboard.writeText(text);
      
      // Success feedback
      copyBtn.classList.add('copied');
      copyBtn.innerHTML = `
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <polyline points="20 6 9 17 4 12" stroke-width="3"/>
        </svg>
      `;
      showNotification('Job description copied to clipboard!', 'success');
      
      setTimeout(() => {
        copyBtn.classList.remove('copied');
        copyBtn.innerHTML = `
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" stroke-width="2"/>
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" stroke-width="2"/>
          </svg>
        `;
      }, 2000);
    } catch (err) {
      showNotification('Failed to copy. Please try again.', 'error');
    }
  });
  
  // Hide on mobile
  if (window.innerWidth >= 768) {
    document.body.appendChild(copyBtn);
  }
}

/**
 * 3. Auto-generate Table of Contents
 * Creates navigation for long job descriptions
 */
function initTableOfContents() {
  const content = document.querySelector('.positivius-job-description, .job-description-content, .cleaned-content');
  if (!content) return;
  
  const headings = content.querySelectorAll('h2, h3');
  if (headings.length < 3) return; // Only show TOC if 3+ headings
  
  // Check if TOC already exists
  if (document.querySelector('.positivius-toc')) return;
  
  // Create TOC container
  const toc = document.createElement('div');
  toc.className = 'positivius-toc';
  toc.style.cssText = `
    position: sticky;
    top: 100px;
    background: white;
    border-radius: 16px;
    padding: 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    max-height: calc(100vh - 140px);
    overflow-y: auto;
    margin-bottom: 24px;
  `;
  
  const tocTitle = document.createElement('div');
  tocTitle.className = 'toc-title';
  tocTitle.textContent = 'Table of Contents';
  tocTitle.style.cssText = `
    font-size: 18px;
    font-weight: 700;
    color: #191A23;
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 2px solid #B9FF66;
  `;
  
  const tocList = document.createElement('ul');
  tocList.className = 'toc-list';
  tocList.style.cssText = `
    list-style: none;
    padding: 0;
    margin: 0;
  `;
  
  headings.forEach((heading, index) => {
    const id = `section-${index}`;
    heading.id = id;
    
    const li = document.createElement('li');
    li.style.marginBottom = '8px';
    
    const a = document.createElement('a');
    a.href = `#${id}`;
    a.textContent = heading.textContent;
    a.style.cssText = `
      display: block;
      padding: 8px 12px;
      color: #6B7280;
      text-decoration: none;
      border-radius: 8px;
      font-size: 14px;
      transition: all 0.3s ease;
      position: relative;
      padding-left: 20px;
    `;
    
    a.addEventListener('click', (e) => {
      e.preventDefault();
      heading.scrollIntoView({ behavior: 'smooth', block: 'start' });
      
      // Update active state
      document.querySelectorAll('.toc-list a').forEach(link => {
        link.style.background = 'transparent';
        link.style.color = '#6B7280';
        link.style.fontWeight = '400';
      });
      a.style.background = 'rgba(185, 255, 102, 0.15)';
      a.style.color = '#191A23';
      a.style.fontWeight = '600';
    });
    
    li.appendChild(a);
    tocList.appendChild(li);
  });
  
  toc.appendChild(tocTitle);
  toc.appendChild(tocList);
  
  // Insert TOC before content (only on desktop)
  if (window.innerWidth > 1200) {
    content.parentElement.insertBefore(toc, content);
  }
}

/**
 * 4. Read Time Estimator
 * Calculate and display estimated reading time
 */
function calculateReadTime() {
  const content = document.querySelector('.positivius-job-description, .job-description-content, .cleaned-content');
  if (!content) return;
  
  const text = content.innerText;
  const wordsPerMinute = 200;
  const wordCount = text.trim().split(/\s+/).length;
  const readTime = Math.ceil(wordCount / wordsPerMinute);
  
  // Check if badge already exists
  if (document.querySelector('.positivius-read-time')) return;
  
  const badge = document.createElement('div');
  badge.className = 'positivius-read-time';
  badge.innerHTML = `
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <circle cx="12" cy="12" r="10" stroke-width="2"/>
      <path d="M12 6v6l4 2" stroke-width="2"/>
    </svg>
    <span>${readTime} min read</span>
  `;
  
  content.parentElement.insertBefore(badge, content);
}

/**
 * 5. NEW Badge for Recent Jobs
 * Add pulsing badge for jobs posted within last 3 days
 */
function initNewBadges() {
  const jobDate = document.querySelector('[datetime]')?.getAttribute('datetime');
  if (!jobDate) return;
  
  const postedDate = new Date(jobDate);
  const now = new Date();
  const daysDiff = Math.floor((now - postedDate) / (1000 * 60 * 60 * 24));
  
  // Show NEW badge if posted within last 3 days
  if (daysDiff <= 3) {
    const title = document.querySelector('.job-title-main, .job-title-details, h1');
    if (title && !document.querySelector('.positivius-new-badge')) {
      const badge = document.createElement('span');
      badge.className = 'positivius-new-badge';
      badge.textContent = 'NEW';
      title.appendChild(badge);
    }
  }
}

/**
 * 6. Share Functionality
 * Native share API or fallback to copy link
 */
function initShareButtons() {
  const shareButtons = document.querySelectorAll('.share-btn, [data-share-job]');
  
  shareButtons.forEach(button => {
    // Avoid double-binding when Turbo re-renders frames
    if (button.dataset.shareBound === 'true') return;
    button.dataset.shareBound = 'true';

    button.addEventListener('click', async (e) => {
      e.preventDefault();
      
      const jobTitle =
        button.dataset.shareTitle ||
        document.querySelector('.job-title-main, .job-title-details, h1')?.textContent ||
        'Job Opportunity';

      // Prefer server-provided canonical job URL (important for Turbo-frame sidebar)
      const jobUrl = button.dataset.shareUrl || window.location.href;
      const provider = button.dataset.shareProvider;

      // Provider-specific actions (used by V3 sidebar buttons)
      if (provider === 'copy') {
        try {
          await navigator.clipboard.writeText(jobUrl);
          showNotification('Link copied to clipboard!', 'success');
        } catch (err) {
          console.error('Failed to copy link:', err);
        }
        return;
      }

      if (provider === 'twitter') {
        const shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(jobUrl)}&text=${encodeURIComponent(jobTitle)}`;
        window.open(shareUrl, '_blank', 'noopener,noreferrer');
        return;
      }

      if (provider === 'linkedin') {
        const shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(jobUrl)}`;
        window.open(shareUrl, '_blank', 'noopener,noreferrer');
        return;
      }

      if (provider === 'email') {
        const mailto = `mailto:?subject=${encodeURIComponent(jobTitle)}&body=${encodeURIComponent(jobUrl)}`;
        window.location.href = mailto;
        return;
      }
      
      if (navigator.share) {
        try {
          await navigator.share({
            title: jobTitle,
            url: jobUrl
          });
          showNotification('Job shared successfully!', 'success');
        } catch (err) {
          if (err.name !== 'AbortError') {
            console.error('Share failed:', err);
          }
        }
      } else {
        // Fallback: Copy link to clipboard
        try {
          await navigator.clipboard.writeText(jobUrl);
          showNotification('Link copied to clipboard!', 'success');
        } catch (err) {
          console.error('Failed to copy link:', err);
        }
      }
    });
  });
}

/**
 * 7. Notification System
 * Show temporary notifications
 */
function showNotification(message, type = 'info') {
  const notification = document.createElement('div');
  notification.className = `positivius-notification ${type}`;
  notification.innerHTML = `
    <div class="notification-content">
      <span class="notification-icon">${type === 'success' ? '✓' : 'ℹ'}</span>
      <span class="notification-message">${message}</span>
    </div>
  `;
  
  document.body.appendChild(notification);
  
  // Animate in
  setTimeout(() => notification.classList.add('show'), 10);
  
  // Remove after 3 seconds
  setTimeout(() => {
    notification.classList.remove('show');
    setTimeout(() => notification.remove(), 300);
  }, 3000);
}

/**
 * 8. Fade-in on Scroll
 * Smooth entrance for content sections
 */
function initScrollAnimations() {
  const sections = document.querySelectorAll('.content-section, .positivius-benefits, .positivius-bottom-apply');
  
  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.style.animation = 'positivius-fade-in-up 0.6s ease forwards';
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1 });
    
    sections.forEach(section => {
      section.style.opacity = '0';
      observer.observe(section);
    });
  }
}

// Export functions for external use
export {
  initPositiviusJobInteractions,
  initScrollProgress,
  initCopyToClipboard,
  initShareButtons,
  initTableOfContents,
  calculateReadTime,
  initNewBadges,
  initScrollAnimations,
  showNotification
};
